import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  useDeletePaymentCredentials,
  useOrderingSettings,
  useUpdateOrderingSettings,
  useUpsertPaymentCredentials,
} from "@/hooks/use-orders";
import { DeliveryConfigCard } from "./components/delivery-config-card";
import { DeliveryPricingCard } from "./components/delivery-pricing-card";
import { buildOrderingPatch, type OrderingCapabilityField } from "./ordering-cascade-model";

export default function OrderingSettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useOrderingSettings();
  const update = useUpdateOrderingSettings();
  const upsert = useUpsertPaymentCredentials();
  const remove = useDeletePaymentCredentials();
  const [accessToken, setAccessToken] = useState("");
  const [publicKey, setPublicKey] = useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return <p className="p-6 text-destructive">{error.message}</p>;
  }

  const settings = data;
  const credentials = settings?.credentials ?? null;
  const orderingEnabled = settings?.orderingEnabled ?? false;
  const deliveryEnabled = settings?.deliveryEnabled ?? false;
  const cashOnDeliveryEnabled = settings?.cashOnDeliveryEnabled ?? false;
  const deliveryFeeFlat = settings?.deliveryFeeFlat ?? 0;
  const deliveryMinimumOrder = settings?.deliveryMinimumOrder ?? 0;

  const handleToggle = (field: OrderingCapabilityField) => async (next: boolean) => {
    const result = buildOrderingPatch(
      { cashOnDeliveryEnabled, deliveryEnabled, orderingEnabled },
      field,
      next,
    );
    try {
      await update.mutateAsync(result.patchBody);
      if (result.isCascade) {
        if (field === "orderingEnabled") {
          toast.success(
            t("orders.settings.cascadeOrderingOff", {
              defaultValue: "Pedidos, Envíos y Pago en la entrega desactivados.",
            }),
          );
        } else {
          toast.success(
            t("orders.settings.cascadeDeliveryOff", {
              defaultValue: "Envíos y Pago en la entrega desactivados.",
            }),
          );
        }
      } else {
        toast.success(t("orders.settings.toggled", { defaultValue: "Configuración guardada" }));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const handleSave = async () => {
    if (!accessToken.trim()) {
      return;
    }
    try {
      await upsert.mutateAsync({
        accessToken: accessToken.trim(),
        publicKey: publicKey.trim() || null,
      });
      setAccessToken("");
      setPublicKey("");
      toast.success(
        t("orders.settings.credentialsSaved", {
          defaultValue: "Credenciales verificadas y guardadas",
        }),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        t("orders.settings.confirmRemove", {
          defaultValue: "¿Eliminar las credenciales? El pago online dejará de estar disponible.",
        }),
      )
    ) {
      return;
    }
    try {
      await remove.mutateAsync();
      toast.success(
        t("orders.settings.credentialsRemoved", {
          defaultValue: "Credenciales eliminadas",
        }),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="font-semibold text-2xl">
        {t("orders.settings.title", {
          defaultValue: "Configuración de pedidos",
        })}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("orders.settings.enableTitle", {
              defaultValue: "Habilitar pedidos",
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="orderingEnabled">
                {t("orders.settings.enableLabel", {
                  defaultValue: "Pedidos",
                })}
              </Label>
              <p className="text-muted-foreground text-sm">
                {t("orders.settings.enableDescription", {
                  defaultValue:
                    "Cuando está habilitado, los clientes pueden pedir desde el menú público.",
                })}
              </p>
            </div>
            <Switch
              checked={orderingEnabled}
              disabled={update.isPending}
              id="orderingEnabled"
              onCheckedChange={handleToggle("orderingEnabled")}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("orders.settings.deliveryTitle", {
              defaultValue: "Envíos",
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="deliveryEnabled">
                {t("orders.settings.deliveryLabel", {
                  defaultValue: "Envíos a domicilio",
                })}
              </Label>
              <p className="text-muted-foreground text-sm">
                {orderingEnabled
                  ? t("orders.settings.deliveryDescription", {
                      defaultValue:
                        "Cuando está habilitado, los clientes pueden pedir envíos a domicilio.",
                    })
                  : t("orders.settings.deliveryRequiresOrdering", {
                      defaultValue: "Requiere que Pedidos esté habilitado.",
                    })}
              </p>
            </div>
            <Switch
              checked={deliveryEnabled}
              disabled={update.isPending || !orderingEnabled}
              id="deliveryEnabled"
              onCheckedChange={handleToggle("deliveryEnabled")}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="cashOnDeliveryEnabled">
                {t("orders.settings.cashOnDeliveryLabel", {
                  defaultValue: "Pago en la entrega",
                })}
              </Label>
              <p className="text-muted-foreground text-sm">
                {deliveryEnabled
                  ? t("orders.settings.cashOnDeliveryDescription", {
                      defaultValue:
                        "Cuando está habilitado, los clientes pueden pagar en efectivo al recibir el pedido.",
                    })
                  : t("orders.settings.cashOnDeliveryRequiresDelivery", {
                      defaultValue: "Requiere que Envíos esté habilitado.",
                    })}
              </p>
            </div>
            <Switch
              checked={cashOnDeliveryEnabled}
              disabled={update.isPending || !deliveryEnabled}
              id="cashOnDeliveryEnabled"
              onCheckedChange={handleToggle("cashOnDeliveryEnabled")}
            />
          </div>
        </CardContent>
      </Card>

      <DeliveryConfigCard
        coverage={settings?.deliveryCoverage ?? null}
        deliveryEnabled={deliveryEnabled}
        origin={settings?.deliveryOrigin ?? null}
      />

      <DeliveryPricingCard
        deliveryEnabled={deliveryEnabled}
        deliveryFeeFlat={deliveryFeeFlat}
        deliveryMinimumOrder={deliveryMinimumOrder}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("orders.settings.mpTitle", {
              defaultValue: "Pago online (Mercado Pago)",
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-sm">
            {t("orders.settings.mpDescription", {
              defaultValue:
                "Pegá tu Access Token de Mercado Pago. El dinero va directo a tu cuenta — Nosotros no lo recibimos.",
            })}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={credentials?.isLive ? "default" : "secondary"}>
              {credentials?.isLive
                ? t("orders.settings.statusLive", { defaultValue: "Activo" })
                : credentials
                  ? t("orders.settings.statusConfigured", {
                      defaultValue: "Configurado, sin verificar",
                    })
                  : t("orders.settings.statusUnconfigured", {
                      defaultValue: "Sin configurar",
                    })}
            </Badge>
            {credentials?.userIdAtProvider ? (
              <span className="text-muted-foreground text-xs">
                MP user: {credentials.userIdAtProvider}
              </span>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="accessToken">
              {t("orders.settings.accessToken", {
                defaultValue: "Access Token",
              })}
            </Label>
            <Input
              id="accessToken"
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="APP_USR-..."
              type="password"
              value={accessToken}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="publicKey">
              {t("orders.settings.publicKey", {
                defaultValue: "Public Key (opcional)",
              })}
            </Label>
            <Input
              id="publicKey"
              onChange={(e) => setPublicKey(e.target.value)}
              placeholder="APP_USR-..."
              value={publicKey}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              disabled={upsert.isPending || !accessToken.trim()}
              onClick={handleSave}
              type="button"
            >
              {upsert.isPending
                ? t("orders.settings.verifying", {
                    defaultValue: "Verificando…",
                  })
                : t("orders.settings.saveAndVerify", {
                    defaultValue: "Guardar y verificar",
                  })}
            </Button>
            {credentials ? (
              <Button
                disabled={remove.isPending}
                onClick={handleRemove}
                type="button"
                variant="destructive"
              >
                {t("orders.settings.removeCredentials", {
                  defaultValue: "Eliminar credenciales",
                })}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
